import { useEffect, useState } from "react";

import { Button, Modal, TextField } from "../../../../ui/index.ts";
import type { DesignationRow } from "../../types.ts";
import styles from "./designation-form-modal.module.css";

export interface DesignationFormModalProps {
  open: boolean;
  designation: DesignationRow | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<boolean | string>;
}

export function DesignationFormModal({
  open,
  designation,
  submitting,
  onClose,
  onSubmit,
}: DesignationFormModalProps) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  const isEdit = designation != null;

  useEffect(() => {
    if (!open) {
      setName("");
      setNameError(undefined);
      return;
    }
    setName(designation?.name ?? "");
    setNameError(undefined);
  }, [open, designation]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Designation name is required");
      return;
    }
    setNameError(undefined);
    const result = await onSubmit(trimmed);
    if (result === true) {
      onClose();
    } else if (typeof result === "string") {
      setNameError(result);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Update Designation" : "Add Designation"}
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="light-grey" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="accent"
            onClick={() => void handleSubmit()}
            disabled={
              submitting || (isEdit && name.trim() === designation?.name)
            }
          >
            {submitting ? "Saving..." : isEdit ? "Update" : "Submit"}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <TextField
          id="designation-name"
          label="Name"
          required
          value={name}
          maxLength={150}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(undefined);
          }}
          error={nameError}
          placeholder="Enter designation name"
        />
      </div>
    </Modal>
  );
}
